import type { MbcBriefAssetTarget } from "@nlc/shared-types";

import { Plus } from "lucide-react";
import { useMemo } from "react";

import { MediaPreviewModalProvider } from "@/components/media-preview-modal/MediaPreviewModalProvider";

import type { BriefMemory } from "../hooks/useBriefMemory";
import type { AssetFileRef, AssetLinkRef } from "./upload-assets/types";

import { useDeleteAsset } from "../hooks/useDeleteAsset";
import { useFilePreview } from "../hooks/useFilePreview";
import { collectFiles } from "../lib/collect-files";
import { AssetThumb } from "./AssetThumb";
import { ScrollFade } from "./ScrollFade";
import { AssetLinkRow } from "./upload-assets/AssetLinkRow";
import { readGroup } from "./upload-assets/types";

function FileGroup({
  files,
  links,
  onPreview,
  onRemove,
  title,
}: {
  files: AssetFileRef[];
  links: AssetLinkRef[];
  onPreview: (attachmentId: number) => void;
  onRemove: (attachmentId: number) => void;
  title: string;
}) {
  return (
    <div className="mt-lg">
      <span className="text-sm font-semibold text-text-primary">{title}</span>
      {files.length > 0 ? (
        <div className="mt-md grid grid-cols-3 gap-sm">
          {files.map((file) => (
            <AssetThumb
              file={file}
              key={file.attachmentId}
              onPreview={onPreview}
              onRemove={onRemove}
            />
          ))}
        </div>
      ) : null}
      {links.length > 0 ? (
        <ul className="mt-md flex flex-col gap-sm">
          {links.map((link) => (
            <AssetLinkRow key={link.url} showShareHint={false} url={link.url} />
          ))}
        </ul>
      ) : null}
      {files.length === 0 && links.length === 0 ? (
        <p className="mt-md text-xs text-text-tertiary">
          No {title.toLowerCase()} yet. Use “Add files” to upload.
        </p>
      ) : null}
    </div>
  );
}

type UploadedFilesPanelProps = {
  canAddFiles: boolean;
  memory: BriefMemory | null;
  /** Opens the upload modal (shared with the chat's attach button). */
  onAddAssets: () => void;
  /** Re-judge the target's grade after one of its files was deleted. */
  onFilesChanged: (target: MbcBriefAssetTarget) => void;
};

export function UploadedFilesPanel(props: UploadedFilesPanelProps) {
  return (
    <MediaPreviewModalProvider>
      <UploadedFilesPanelContent {...props} />
    </MediaPreviewModalProvider>
  );
}

function UploadedFilesPanelContent({
  canAddFiles,
  memory,
  onAddAssets,
  onFilesChanged,
}: UploadedFilesPanelProps) {
  const assetFiles = useMemo(() => collectFiles(memory?.assets), [memory]);
  const inspirationFiles = useMemo(
    () => collectFiles(memory?.inspirations),
    [memory],
  );
  const assetLinks = useMemo(() => readGroup(memory?.assets).links, [memory]);
  const inspirationLinks = useMemo(
    () => readGroup(memory?.inspirations).links,
    [memory],
  );
  const previewFile = useFilePreview(
    useMemo(
      () => [...assetFiles, ...inspirationFiles],
      [assetFiles, inspirationFiles],
    ),
  );
  const removeAsset = useDeleteAsset(() => onFilesChanged("assets"));
  const removeInspiration = useDeleteAsset(() =>
    onFilesChanged("inspirations"),
  );

  return (
    <aside className="flex min-h-[160px] w-full max-w-[320px] shrink-[100] flex-col rounded-2xl border border-border-secondary bg-bg-primary p-xl">
      <div className="flex shrink-0 items-center justify-between">
        <h2 className="text-md font-semibold text-text-primary">
          Uploaded files
        </h2>
        <button
          className="flex items-center gap-1 text-sm font-semibold text-fg-brand-primary outline-hidden transition-colors hover:underline disabled:cursor-not-allowed disabled:text-fg-disabled disabled:no-underline"
          disabled={!canAddFiles}
          onClick={onAddAssets}
          type="button"
        >
          <Plus className="size-4" />
          Add files
        </button>
      </div>

      <ScrollFade>
        <FileGroup
          files={assetFiles}
          links={assetLinks}
          onPreview={previewFile}
          onRemove={removeAsset}
          title="Assets"
        />
        <FileGroup
          files={inspirationFiles}
          links={inspirationLinks}
          onPreview={previewFile}
          onRemove={removeInspiration}
          title="Inspirations"
        />
      </ScrollFade>
    </aside>
  );
}
